"use client"

import * as React from "react"
import { useSearchParams } from "react-router-dom"
import { useTranslations } from "@/i18n"
import { ExportAction, exportFileName } from "@/shared/components/export"
import { moneyToNumberForChartGeometryOnly } from "@ibada/shared-types/finance"
import { zodResolver } from "@hookform/resolvers/zod"
import { type ColumnDef } from "@tanstack/react-table"
import {
  ArchiveIcon,
  ArrowLeftRightIcon,
  EyeIcon,
  Link2Icon,
  PencilIcon,
  PlusIcon,
  RotateCcwIcon,
  ScanSearchIcon,
  Trash2Icon,
} from "lucide-react"
import { useForm } from "react-hook-form"
import { toast } from "sonner"

import {
  FinanceActivityStatus,
  FinanceCategoryScope,
  FinanceFundListKind,
  FinanceFundKind,
  FinanceFundStatus,
  type FinanceFundRecord,
} from "@/features/finance/model/finance-types"
import {
  formatMoney,
  getCurrencyPrefix,
  isPositiveMoney,
  isZeroOrNegativeMoney,
  toMoneyOrNull,
} from "@/features/finance/lib/finance-utils"
import { useFinancePageState } from "@/features/finance/hooks/use-finance-page-state"
import { useInvestigate } from "@/features/finance/components/audit/investigate-button"
import { FundFormDialog, mapFundToFormValues } from "@/features/finance/components/funds/fund-form-dialog"
import { FundDetailsSheet } from "@/features/finance/components/funds/fund-details-sheet"
import { FundCardGrid, FundMark } from "@/features/finance/components/funds/fund-card-grid"
import {
  buildFinanceFundFormSchema,
  createDefaultFinanceFundFormValues,
  type FinanceFundFormInput,
  type FinanceFundFormValues,
} from "@/features/finance/schemas/finance-configuration-forms"
import {
  useFinanceActivitiesQuery,
  useFinanceFundsQuery,
  useReallocateFundsMutation,
  useFinanceSetFundStatusMutation,
  useFinanceAttachFundActivitiesMutation,
} from "@/features/finance/services/finance-query"
import { useApiErrorToast } from "@/shared/hooks/use-api-error"
import { dataTableOverflowActionsColumnSizing } from "@/shared/lib/data-table/data-table-overflow-actions-column"
import {
  Badge,
  Button,
  ConfirmActionDialog,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  HelperMessage,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
  Select,
  cn,
  valueLinkClass,
} from "@ibada/ui"
import {
  DataTableMobileListVariant,
  DataTablePagination,
  DataTableTruncatedTextCell,
  DataTableVariant,
} from "@ibada/ui/components/data-table"
import { useTableQuery } from "@/shared/lib/data-table/table-query"
import { fundsTableQueryConfig } from "@/features/finance/model/funds-table-config"
import { toFundsListQuery } from "@/features/finance/lib/funds-table-query"
import { useBranchCurrency } from "@/features/finance/hooks/use-branch-currency"
import { useMobileListChrome } from "@/shared/components/mobile-list-chrome"
import { useIsMobile } from "@/shared/hooks/use-mobile"
import { RowActionsMenu, type RowActionsMenuItem } from "@/shared/components/row-actions-menu"
import {
  AliveActionsCell,
  AliveCountCell,
  AliveLeadCell,
  AlivePillTone,
  AliveProgressCell,
  AliveStatusCell,
} from "@/shared/components/alive-cells"
import { useFormatMoneyText } from "@/shared/components/mobile-form"

type FundDialogState = { mode: "create"; record: null } | { mode: "edit"; record: FinanceFundRecord }



/** An unknown or absent `?kind=` means the unfiltered view — never an empty table. */
function parseFundKindTab(value: string | null | undefined): FinanceFundListKind {
  const known = Object.values(FinanceFundListKind) as string[]
  return value && known.includes(value) ? (value as FinanceFundListKind) : FinanceFundListKind.All
}

/**
 * One fund on a phone: the name with a short status line, the balance on the right, and a thin progress bar
 * when the fund has a target. Also heads the fund's actions sheet.
 */
function FundMobileRow({ fund, currencyCode }: { fund: FinanceFundRecord; currencyCode: string }) {
  const t = useTranslations("finance.funds")
  const formatMoneyText = useFormatMoneyText(currencyCode)
  const target = fund.targetAmount
  const hasTarget = target !== null && !isZeroOrNegativeMoney(target, currencyCode)
  // Geometry only (the bar's width); the figures shown stay the exact strings.
  const percent = hasTarget
    ? Math.max(0, Math.round((moneyToNumberForChartGeometryOnly(fund.balance) / moneyToNumberForChartGeometryOnly(target)) * 100))
    : 0
  const detail = [
    fund.status !== FinanceFundStatus.Active ? t(`status.${fund.status.toLowerCase()}` as never) : null,
    fund.kind === FinanceFundKind.SystemLinked ? t("systemManaged") : null,
    hasTarget ? `${percent} % · ${formatMoneyText(target)}` : null,
  ]
    .filter(Boolean)
    .join(" · ")
  return (
    <div className='flex min-w-0 flex-1 flex-col gap-1.5'>
      <div className='flex min-w-0 items-center gap-3'>
        <span className='flex min-w-0 flex-1 flex-col gap-0.5'>
          <span className='truncate text-[15px] leading-tight font-extrabold text-foreground'>{fund.name}</span>
          {detail ? <span className='truncate text-xs font-semibold text-muted-foreground'>{detail}</span> : null}
        </span>
        <span className='max-w-[45%] shrink-0 truncate text-right text-[15px] font-extrabold tabular-nums text-foreground'>
          {formatMoneyText(fund.balance)}
        </span>
      </div>
      {hasTarget ? (
        <div className='h-1 w-full overflow-hidden rounded-full bg-muted'>
          <div className='h-full rounded-full bg-primary' style={{ width: `${Math.min(100, percent)}%` }} />
        </div>
      ) : null}
    </div>
  )
}

const FUND_STATUS_PILL: Record<FinanceFundStatus, AlivePillTone> = {
  [FinanceFundStatus.Active]: AlivePillTone.Success,
  [FinanceFundStatus.Closed]: AlivePillTone.Danger,
  [FinanceFundStatus.Archived]: AlivePillTone.Neutral,
}

export function FinanceFundsPageContent() {
  const t = useTranslations("finance.funds")
  const tCommon = useTranslations("common")
  const tAudit = useTranslations("finance.audit")
  const investigate = useInvestigate()
  const toastError = useApiErrorToast("finance")

  const { workspaceId, activeContext, scopeBranch, canEditFinance } =
    useFinancePageState()
  const canEdit = canEditFinance
  const branchId = scopeBranch?.id ?? null
  const baseCurrency = useBranchCurrency()

  const schema = React.useMemo(
    () => buildFinanceFundFormSchema((key, values) => t(key as never, values as never), baseCurrency),
    [baseCurrency, t],
  )

  const [dialogState, setDialogState] = React.useState<FundDialogState | null>(null)
  const [detailsFund, setDetailsFund] = React.useState<FinanceFundRecord | null>(null)
  const [pendingClose, setPendingClose] = React.useState<FinanceFundRecord | null>(null)
  const [attachTarget, setAttachTarget] = React.useState<FinanceFundRecord | null>(null)
  // Reallocation (IBA-154 D9): move a designation OUT of this fund and into another. No physical money moves.
  const [reallocateSource, setReallocateSource] = React.useState<FinanceFundRecord | null>(null)
  const [reallocateDestinationId, setReallocateDestinationId] = React.useState("")
  const [reallocateAmount, setReallocateAmount] = React.useState("")
  const [attachActivityIds, setAttachActivityIds] = React.useState<string[]>([])
  // The fund's activity set as it was when the dialog opened — to detect whether anything changed.
  const [attachOriginalIds, setAttachOriginalIds] = React.useState<string[]>([])

  const form = useForm<FinanceFundFormValues, undefined, FinanceFundFormInput>({
    resolver: zodResolver(schema),
    defaultValues: createDefaultFinanceFundFormValues(),
  })

  const { queryState, filterConfig } = useTableQuery({
    config: fundsTableQueryConfig,
    t,
    lookupScope: {
      workspaceId: workspaceId ?? undefined,
      branchId: branchId ?? undefined,
    },
  })

  /**
   * Toolbar tab, held in the URL (`?kind=`) so the view is shareable and survives a reload — the same rule the
   * transfers tabs follow.
   *
   * It exists because D9 auto-creates one SYSTEM_LINKED fund per income account: a workspace has seventeen of
   * them before anyone defines a fund of their own, and the handful of CUSTOM funds a church actually created
   * are lost in the list. `All` stays the default so nothing is hidden by surprise.
   */
  const [searchParams, setSearchParams] = useSearchParams()
  const activeKind = React.useMemo(() => parseFundKindTab(searchParams.get("kind")), [searchParams])
  const setActiveKind = React.useCallback(
    (next: FinanceFundListKind) => {
      const params = new URLSearchParams(searchParams)
      if (next === FinanceFundListKind.All) params.delete("kind")
      else params.set("kind", next)
      setSearchParams(params, { replace: true })
    },
    [searchParams, setSearchParams],
  )

  const queryParams = queryState.toQueryParams()

  const fundsQuery = useFinanceFundsQuery(
    workspaceId && (activeContext === "parish" || branchId)
      ? toFundsListQuery({
          queryParams,
          workspaceId,
          branchId,
          scope: activeContext === "branch" ? FinanceCategoryScope.Branch : FinanceCategoryScope.Parish,
          kind: activeKind,
        })
      : null,
  )

  const reallocateMutation = useReallocateFundsMutation()
  const allocateScope = activeContext === "branch" ? FinanceCategoryScope.Branch : FinanceCategoryScope.Parish

  // Possible destinations: any OTHER active CUSTOM fund. A fund cannot receive its own money, and a
  // SYSTEM_LINKED fund is excluded because its balance is what its income category actually received — money
  // arriving from elsewhere would make the funds screen and the offering report disagree. The backend enforces
  // both (DESTINATION_FUND_IS_LINKED); this only keeps the operator from picking something that will be refused.
  const reallocateDestinationOptions = React.useMemo(
    () =>
      (fundsQuery.data?.items ?? [])
        .filter(
          (fund) =>
            fund.id !== reallocateSource?.id &&
            fund.status === FinanceFundStatus.Active &&
            fund.kind !== FinanceFundKind.SystemLinked,
        )
        .map((fund) => ({ value: fund.id, label: fund.name })),
    [fundsQuery.data?.items, reallocateSource?.id],
  )

  // The amount typed into the dialog, as money — `null` while it is half-typed or over-precise.
  const reallocateMoney = toMoneyOrNull(reallocateAmount, baseCurrency)
  const canReallocate =
    reallocateMoney !== null &&
    isPositiveMoney(reallocateMoney, baseCurrency) &&
    Boolean(reallocateDestinationId)

  const handleReallocate = async () => {
    if (!canEdit || !workspaceId || !reallocateSource || reallocateMoney === null) return
    try {
      await reallocateMutation.mutateAsync({
        workspaceId,
        branchId,
        scope: allocateScope,
        sourceFundId: reallocateSource.id,
        destinationFundId: reallocateDestinationId,
        amount: reallocateMoney,
      })
      toast.success(t("reallocate.toast"))
      setReallocateSource(null)
    } catch (error) {
      toastError(error)
    }
  }
  const setFundStatusMutation = useFinanceSetFundStatusMutation()
  const attachActivitiesMutation = useFinanceAttachFundActivitiesMutation()

  // The fund's already-attached activities (any status) pre-fill the rows — filtered by the request on fundId.
  const attachActivitiesQuery = useFinanceActivitiesQuery(
    attachTarget && workspaceId ? { workspaceId, branchId: attachTarget.branchId, fundId: attachTarget.id, limit: 100 } : null,
  )
  const attachActivities = React.useMemo(() => attachActivitiesQuery.data ?? [], [attachActivitiesQuery.data])
  const attachActivitiesById = React.useMemo(
    () => new Map(attachActivities.map((activity) => [activity.id, activity])),
    [attachActivities],
  )
  // "Add" candidates — ACTIVE activities in the branch, filtered by the request (activeOnly), not the client.
  const addCandidatesQuery = useFinanceActivitiesQuery(
    attachTarget && workspaceId
      ? { workspaceId, branchId: attachTarget.branchId, activeOnly: true, limit: 100 }
      : null,
  )
  const addActivitiesById = React.useMemo(
    () => new Map((addCandidatesQuery.data ?? []).map((activity) => [activity.id, activity])),
    [addCandidatesQuery.data],
  )
  const addActivityOptions = React.useMemo(
    () =>
      (addCandidatesQuery.data ?? [])
        .filter((activity) => !attachActivityIds.includes(activity.id))
        .map((activity) => ({ value: activity.id, label: activity.title })),
    [addCandidatesQuery.data, attachActivityIds],
  )

  // Pre-fill the rows with the fund's currently-attached activities once, when the dialog's data loads.
  const attachPrefilledFor = React.useRef<string | null>(null)
  React.useEffect(() => {
    if (!attachTarget) {
      attachPrefilledFor.current = null
      return
    }
    if (attachPrefilledFor.current === attachTarget.id || attachActivitiesQuery.data === undefined) return
    attachPrefilledFor.current = attachTarget.id
    // The query is already filtered to this fund's activities, so every row is a current link.
    const currentIds = attachActivities.map((activity) => activity.id)
    setAttachActivityIds(currentIds)
    setAttachOriginalIds(currentIds)
  }, [attachTarget, attachActivities, attachActivitiesQuery.data])

  // Whether the selection differs from what was attached when the dialog opened.
  const attachHasChanges = React.useMemo(() => {
    if (attachActivityIds.length !== attachOriginalIds.length) return true
    const original = new Set(attachOriginalIds)
    return attachActivityIds.some((id) => !original.has(id))
  }, [attachActivityIds, attachOriginalIds])

  // Viewing the activities linked to a fund (click the connected-activities pill).
  const [viewActivitiesFund, setViewActivitiesFund] = React.useState<FinanceFundRecord | null>(null)
  const viewActivitiesQuery = useFinanceActivitiesQuery(
    viewActivitiesFund && workspaceId
      ? { workspaceId, branchId: viewActivitiesFund.branchId, fundId: viewActivitiesFund.id, limit: 100 }
      : null,
  )
  const linkedActivities = React.useMemo(() => viewActivitiesQuery.data ?? [], [viewActivitiesQuery.data])

  // Activities to (optionally) attach while CREATING a fund — same candidates, scoped to the active branch.

  // categories→accounts: a restricted fund's allow-list is EXPENSE AccountingAccounts, listed from the Chart of
  // Accounts (type EXPENSE), shown with their code as a prefix ("6xx Name").

  const closeAttachDialog = React.useCallback(() => {
    setAttachTarget(null)
    setAttachActivityIds([])
    setAttachOriginalIds([])
    attachPrefilledFor.current = null
  }, [])

  const handleAttachActivities = React.useCallback(async () => {
    if (!canEdit || !attachTarget) return
    try {
      const result = await attachActivitiesMutation.mutateAsync({
        id: attachTarget.id,
        activityIds: attachActivityIds,
      })
      toast.success(t("attach.toastSuccess", { count: result.attached }))
      closeAttachDialog()
    } catch (error) {
      toastError(error)
    }
  }, [attachActivitiesMutation, attachActivityIds, attachTarget, canEdit, closeAttachDialog, t, toastError])


  const funds = fundsQuery.data?.items ?? []
  const fundsTotal = fundsQuery.data?.total ?? funds.length
  const fundsServerPage = fundsQuery.data?.page ?? queryParams.page ?? 1
  const fundsServerLimit = fundsQuery.data?.limit ?? queryParams.limit ?? 20


  // The dialog owns its own form and reseeds on open, so closing is all this has left to do.
  const resetDialog = React.useCallback(() => setDialogState(null), [])

  function openCreateDialog() {
    if (!canEdit) return
    setDialogState({ mode: "create", record: null })
  }

  const openEditDialog = React.useCallback((record: FinanceFundRecord) => {
    if (!canEdit) return
    setDialogState({ mode: "edit", record })
  }, [canEdit])

  // A custom fund's actions, only those that apply to it now: the phone sheet and the desktop row's ⋯ menu.
  const fundActionGroups = React.useCallback(
    (fund: FinanceFundRecord): RowActionsMenuItem[][] => {
      const active = fund.status === FinanceFundStatus.Active
      return [
        [
          { id: "details", label: t("actions.viewDetails"), icon: EyeIcon, onSelect: () => setDetailsFund(fund) },
          { id: "audit", label: tAudit("investigate"), icon: ScanSearchIcon, onSelect: () => investigate({ fundId: fund.id, label: fund.name }) },
        ],
        canEdit && active
          ? [
              { id: "edit", label: t("actions.edit"), icon: PencilIcon, onSelect: () => openEditDialog(fund) },
              // A restriction is the donor's condition on the money, so it cannot be moved to another purpose
              // (the backend refuses with FUND_RESTRICTED_CANNOT_REALLOCATE).
              ...(fund.isRestricted
                ? []
                : [
                    {
                      id: "reallocate",
                      label: t("reallocate.action"),
                      icon: ArrowLeftRightIcon,
                      onSelect: () => {
                        setReallocateDestinationId("")
                        setReallocateAmount("")
                        setReallocateSource(fund)
                      },
                    },
                  ]),
              {
                id: "attach",
                label: t("actions.attachActivities"),
                icon: Link2Icon,
                onSelect: () => {
                  setAttachActivityIds([])
                  setAttachTarget(fund)
                },
              },
            ]
          : [],
        canEdit && fund.status === FinanceFundStatus.Closed
          ? [
              {
                id: "reopen",
                label: t("actions.reopen"),
                icon: RotateCcwIcon,
                onSelect: () => {
                  void setFundStatusMutation
                    .mutateAsync({ id: fund.id, status: FinanceFundStatus.Active })
                    .then(() => toast.success(t("toasts.reopened")))
                    .catch((error) => toastError(error))
                },
              },
            ]
          : [],
        canEdit && active
          ? [{ id: "close", label: t("actions.close"), icon: ArchiveIcon, destructive: true, onSelect: () => setPendingClose(fund) }]
          : [],
      ]
    },
    [canEdit, investigate, openEditDialog, setFundStatusMutation, t, tAudit, toastError],
  )

  React.useEffect(() => {
    if (!dialogState) {
      form.reset(createDefaultFinanceFundFormValues())
      return
    }

    form.reset(mapFundToFormValues(dialogState.mode === "edit" ? dialogState.record : null))
  }, [dialogState, form])

  const isMobile = useIsMobile()
  // A tap or a click opens the fund (its details sheet), or its transactions for a system-linked fund.
  const openFund = React.useCallback(
    (fund: FinanceFundRecord) =>
      fund.kind === FinanceFundKind.Custom ? setDetailsFund(fund) : investigate({ fundId: fund.id, label: fund.name }),
    [investigate],
  )
  const columns = React.useMemo<ColumnDef<FinanceFundRecord>[]>(
    () => [
      {
        accessorKey: "name",
        header: t("fields.name"),
        size: 300,
        minSize: 220,
        // The name opens the fund (its details, or its transactions for a system-linked fund), with what it is
        // for under it; a system-linked fund says so there instead of in a badge.
        cell: ({ row }) => {
          const fund = row.original
          const subtitle = [fund.kind === FinanceFundKind.SystemLinked ? t("systemManaged") : null, fund.description?.trim() || null]
            .filter(Boolean)
            .join(" · ")
          return (
            <AliveLeadCell
              leading={<FundMark fund={fund} />}
              title={fund.name}
              subtitle={subtitle || null}
              onOpen={() => openFund(fund)}
            />
          )
        },
      },
      {
        accessorKey: "description",
        header: t("fields.description"),
        // Already under the name; still in the column picker for those who want it on its own.
        meta: { defaultHidden: true },
        cell: ({ row }) => (
          <DataTableTruncatedTextCell text={row.original.description} emptyText={t("emptyDescription")} />
        ),
      },
      {
        id: "restriction",
        header: t("fields.restriction"),
        meta: { label: t("fields.restriction") },
        // A restriction is the donor's condition on the money (it cannot be reallocated), so it gets a warning
        // tone; a free fund stays neutral.
        cell: ({ row }) =>
          row.original.isRestricted ? (
            <AliveStatusCell tone={AlivePillTone.Warning}>{t("restrictionPill.restricted")}</AliveStatusCell>
          ) : (
            <AliveStatusCell tone={AlivePillTone.Neutral}>{t("restrictionPill.unrestricted")}</AliveStatusCell>
          ),
      },
      {
        accessorKey: "balance",
        header: t("fields.balance"),
        // `meta.align` right-aligns both the header and the cells (the shared DataTable owns this).
        meta: { label: t("fields.balance"), align: "right" },
        cell: ({ row }) => (
          // A fund's balance is a claim about restricted money: the transactions behind it are the proof. An
          // empty fund steps back so the ones holding money stand out.
          <button
            type='button'
            onClick={() => investigate({ fundId: row.original.id, label: row.original.name })}
            className={cn(
              valueLinkClass,
              "text-[13px] font-extrabold tabular-nums",
              !/[1-9]/.test(row.original.balance) && "text-muted-foreground font-semibold",
            )}
          >
            {formatMoney(row.original.balance, baseCurrency)}
          </button>
        ),
      },
      {
        id: "progress",
        header: t("fields.target"),
        minSize: 200,
        size: 240,
        cell: ({ row }) => {
          const target = row.original.targetAmount
          if (target === null || isZeroOrNegativeMoney(target, baseCurrency)) {
            return <span className='text-muted-foreground text-xs font-semibold'>{t("noTarget")}</span>
          }
          // The BAR's width is geometry (a pixel, not a cent), so a float is fine for it; the amounts beside it
          // are still rendered from the exact decimal strings.
          const percent = Math.max(
            0,
            Math.round(
              (moneyToNumberForChartGeometryOnly(row.original.balance) / moneyToNumberForChartGeometryOnly(target)) * 100,
            ),
          )
          return (
            <AliveProgressCell
              value={formatMoney(row.original.balance, baseCurrency)}
              target={formatMoney(target, baseCurrency)}
              percentage={percent}
            />
          )
        },
      },
      {
        accessorKey: "status",
        header: t("statusLabel"),
        cell: ({ row }) => (
          <AliveStatusCell tone={FUND_STATUS_PILL[row.original.status] ?? AlivePillTone.Neutral}>
            {t(`status.${row.original.status.toLowerCase()}` as never)}
          </AliveStatusCell>
        ),
      },
      {
        id: "activities",
        header: t("fields.linkedActivities"),
        cell: ({ row }) => {
          const count = row.original.activityCount
          if (count === 0) return <AliveCountCell count={0} />
          return (
            <button
              type='button'
              className={cn(valueLinkClass, "cursor-pointer")}
              onClick={() => setViewActivitiesFund(row.original)}
              aria-label={t("linkedActivities.view", { count })}
            >
              <AliveCountCell count={t("linkedActivities.count", { count })} />
            </button>
          )
        },
      },
      {
        id: "actions",
        ...dataTableOverflowActionsColumnSizing,
        header: () => <div className='text-right'>{t("actionsLabel")}</div>,
        // A linked fund mirrors its income category: it is maintained by the engine, so it has no
        // edit/attach/close actions (rename or close the CATEGORY instead). Its name already says
        // "System managed".
        cell: ({ row }) =>
          row.original.kind === FinanceFundKind.SystemLinked ? null : isMobile ? (
            <RowActionsMenu
              label={t("actionsLabel")}
              openLabel={t("actions.open")}
              sheetHeader={<FundMobileRow fund={row.original} currencyCode={baseCurrency} />}
              groups={fundActionGroups(row.original)}
            />
          ) : (
            <AliveActionsCell
              label={t("actionsLabel")}
              openLabel={t("actions.open")}
              sheetHeader={row.original.name}
              groups={fundActionGroups(row.original)}
            />
          ),
      },
    ],
    [baseCurrency, fundActionGroups, investigate, isMobile, openFund, t],
  )

  // Phone, inside Accounts / Funds: the pair's title row (⋯ and + beside it) and its segmented tabs.
  const mobileChrome = useMobileListChrome()

  return (
    <section className='space-y-4' data-testid='finance-funds-page'>
      <div className='space-y-3'>
        <DataTable
          data={funds}
          columns={columns}
          variant={DataTableVariant.Alive}
          initialColumnPinning={{ right: ["actions"] }}
          disablePinningPersistence
          sortingMode='server'
          sortingState={queryState.sortingState}
          onSortingStateChange={queryState.setSortingFromTable}
          persistPaginationInUrl={false}
          hidePagination
          isLoading={fundsQuery.isLoading}
          emptyMessage={t("emptyTable")}
          initialPagination={{ pageIndex: 0, pageSize: fundsServerLimit }}
          mobileListVariant={DataTableMobileListVariant.Grouped}
          renderMobileRow={(row) => <FundMobileRow fund={row.original} currencyCode={baseCurrency} />}
          // Desktop: cards instead of a sparse wide table (a few funds, each a balance and a goal).
          renderDesktopBody={(rows, state) => (
            <FundCardGrid
              funds={rows.map((row) => row.original)}
              currency={baseCurrency}
              isInitialLoading={state.isInitialLoading}
              emptyState={state.emptyState}
              onOpen={openFund}
              onInvestigate={(fund) => investigate({ fundId: fund.id, label: fund.name })}
              onViewActivities={setViewActivitiesFund}
              actionGroups={(fund) => (fund.kind === FinanceFundKind.SystemLinked ? null : fundActionGroups(fund))}
            />
          )}
          // Phone: a tap opens the fund (its details sheet), or its transactions for a system-linked fund.
          onRowClick={isMobile ? openFund : undefined}
          toolbar={{
            ...(isMobile && mobileChrome ? mobileChrome : {}),
            views: {
              value: activeKind,
              onValueChange: (value) => setActiveKind(parseFundKindTab(value)),
              items: [
                { value: FinanceFundListKind.All, label: t("views.all") },
                { value: FinanceFundListKind.Custom, label: t("views.custom") },
              ],
            },
            search: filterConfig.search
              ? {
                  value: filterConfig.search.value,
                  onValueChange: filterConfig.search.onChange,
                  placeholder: filterConfig.search.placeholder ?? t("searchPlaceholder"),
                  label: t("searchPlaceholder"),
                  debounceMs: filterConfig.search.debounceMs,
                }
              : undefined,
            sorting: {
              label: t("toolbar.sort"),
              ascendingLabel: t("toolbar.ascending"),
              descendingLabel: t("toolbar.descending"),
              items: [
                { id: "name", label: t("toolbar.sortByName") },
                { id: "createdAt", label: t("toolbar.sortByCreatedAt") },
                { id: "updatedAt", label: t("toolbar.lastUpdated") },
              ],
            },
            filters: {
              config: filterConfig,
              appliedValues: queryState.filters,
              onApply: queryState.setFilters,
              onReset: queryState.resetFilters,
            },
            // No column picker: desktop draws cards, which have no columns to pick from.
            secondaryActions: [
              {
                id: "export",
                // Phone: a round download icon in the title row (it opens the formats in a sheet).
                mobileIconOnly: true,
                label: tCommon("export.button"),
                emphasis: "priority" as const,
                order: 5,
                render: ({ inOverflow }: { inOverflow: boolean }) => (
                  <ExportAction
                    renderAs={inOverflow ? "menuItem" : "button"}
                    itemCount={fundsTotal}
                    config={{
                      resource: "funds",
                      fileName: exportFileName(t("title")),
                      query: { workspaceId: workspaceId ?? undefined, branchId: branchId ?? undefined },
                    }}
                  />
                ),
              },
            ],
            primaryAction: canEdit
              ? {
                  id: "create",
                  label: t("actions.create"),
                  icon: <PlusIcon className='size-4' />,
                  emphasis: "primary",
                  disabled: !workspaceId || (activeContext === "branch" && !branchId),
                  onSelect: openCreateDialog,
                  testId: "finance-funds-add-button",
                }
              : undefined,
          }}
          footer={
            <DataTablePagination
              server={{
                page: fundsServerPage,
                pageSize: fundsServerLimit,
                totalCount: fundsTotal,
                totalPages: Math.max(Math.ceil(fundsTotal / fundsServerLimit), 1),
                canPreviousPage: fundsServerPage > 1,
                canNextPage: fundsServerPage * fundsServerLimit < fundsTotal,
                onPreviousPage: () => queryState.setPage(fundsServerPage - 1),
                onNextPage: () => queryState.setPage(fundsServerPage + 1),
                onPageSizeChange: (nextPageSize: number) => queryState.setLimit(nextPageSize),
              }}
            />
          }
        />
      </div>

      <FundFormDialog
        open={Boolean(dialogState)}
        onOpenChange={(open) => !open && resetDialog()}
        mode={dialogState?.mode ?? "create"}
        record={dialogState?.mode === "edit" ? dialogState.record : null}
        workspaceId={workspaceId}
        branchId={branchId}
        scope={activeContext === "branch" ? FinanceCategoryScope.Branch : FinanceCategoryScope.Parish}
        baseCurrency={baseCurrency}
      />

      <FundDetailsSheet
        fund={detailsFund}
        open={Boolean(detailsFund)}
        onOpenChange={(open) => !open && setDetailsFund(null)}
        canEdit={canEdit}
        currency={baseCurrency}
        onEdit={openEditDialog}
      />

      <Dialog open={Boolean(attachTarget)} onOpenChange={(open) => !open && closeAttachDialog()}>
        <DialogContent data-testid='finance-funds-attach-dialog'>
          <DialogHeader>
            <DialogTitle>{t("attach.title")}</DialogTitle>
            <DialogDescription>{t("attach.description", { name: attachTarget ? attachTarget.name : "" })}</DialogDescription>
          </DialogHeader>

          <div className='space-y-3'>
            {attachActivityIds.length > 0 ? (
              <ul className='divide-border max-h-56 divide-y overflow-y-auto rounded-md border'>
                {attachActivityIds.map((id) => {
                  const activity = attachActivitiesById.get(id) ?? addActivitiesById.get(id)
                  return (
                    <li
                      key={id}
                      className='hover:bg-muted/50 flex items-center justify-between gap-2 py-1 pl-3 pr-3 transition-colors'
                    >
                      <span className='truncate text-sm'>{activity?.title ?? id}</span>
                      <Button
                        type='button'
                        variant='ghost'
                        size='icon-sm'
                        className='text-destructive hover:text-destructive hover:bg-destructive/10'
                        aria-label={t("attach.remove")}
                        onClick={() => setAttachActivityIds((prev) => prev.filter((x) => x !== id))}
                      >
                        <Trash2Icon className='size-4' />
                      </Button>
                    </li>
                  )
                })}
              </ul>
            ) : (
              <HelperMessage tone='info'>{t("attach.emptyRows")}</HelperMessage>
            )}

            <Select
              isSearchable
              options={addActivityOptions}
              value={undefined}
              onValueChange={(value) => {
                if (value) setAttachActivityIds((prev) => (prev.includes(value) ? prev : [...prev, value]))
              }}
              placeholder={t("attach.addPlaceholder")}
              disabled={attachActivitiesQuery.isLoading || addActivityOptions.length === 0}
            />
          </div>

          <DialogFooter>
            <Button
              type='button'
              variant='secondary'
              onClick={closeAttachDialog}
              disabled={attachActivitiesMutation.isPending}
            >
              {t("actions.cancel")}
            </Button>
            <Button
              type='button'
              onClick={() => void handleAttachActivities()}
              disabled={attachActivitiesMutation.isPending || !attachHasChanges}
            >
              {attachActivitiesMutation.isPending ? t("actions.saving") : t("attach.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(viewActivitiesFund)} onOpenChange={(open) => !open && setViewActivitiesFund(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("linkedActivities.title")}</DialogTitle>
            <DialogDescription>
              {t("linkedActivities.description", { name: viewActivitiesFund ? viewActivitiesFund.name : "" })}
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-2'>
            {viewActivitiesQuery.isLoading ? (
              <p className='text-muted-foreground text-sm'>{t("actions.saving")}</p>
            ) : linkedActivities.length === 0 ? (
              <p className='text-muted-foreground text-sm'>{t("linkedActivities.empty")}</p>
            ) : (
              <ul className='divide-border max-h-72 divide-y overflow-y-auto rounded-md border'>
                {linkedActivities.map((activity) => (
                  <li
                    key={activity.id}
                    className='hover:bg-muted/50 flex items-center justify-between gap-2 py-1.5 pl-3 pr-2 text-sm transition-colors'
                  >
                    <span className='truncate'>{activity.title}</span>
                    <Badge variant={activity.status === FinanceActivityStatus.Active ? "success" : "outline"}>
                      {activity.status}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <DialogFooter>
            <Button type='button' variant='secondary' onClick={() => setViewActivitiesFund(null)}>
              {t("actions.cancel")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* IBA-154 (D9): move a designation from this fund to another. No physical money moves — the cash stays
          where it is and income history is untouched; only what it is FOR changes. */}
      <Dialog open={Boolean(reallocateSource)} onOpenChange={(open) => !open && setReallocateSource(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("reallocate.title")}</DialogTitle>
            <DialogDescription>
              {t("reallocate.description", { name: reallocateSource ? reallocateSource.name : "" })}
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-3'>
            <div className='space-y-1.5'>
              <label className='text-sm font-medium'>{t("reallocate.destinationFund")}</label>
              <Select
                isSearchable
                options={reallocateDestinationOptions}
                value={reallocateDestinationId || undefined}
                onValueChange={(value) => setReallocateDestinationId(typeof value === "string" ? value : "")}
                placeholder={t("reallocate.destinationFundPlaceholder")}
              />
            </div>
            <div className='space-y-1.5'>
              <label className='text-sm font-medium'>{t("reallocate.amount")}</label>
              <InputGroup>
                <InputGroupAddon align='inline-start'>
                  <InputGroupText>{getCurrencyPrefix(baseCurrency)}</InputGroupText>
                </InputGroupAddon>
                <InputGroupInput
                  type='number'
                  min={0}
                  step='any'
                  inputMode='decimal'
                  value={reallocateAmount}
                  onChange={(event) => setReallocateAmount(event.target.value)}
                />
              </InputGroup>
            </div>
            <HelperMessage tone='info' size='sm'>{t("reallocate.hint")}</HelperMessage>
          </div>
          <DialogFooter>
            <Button
              type='button'
              variant='secondary'
              onClick={() => setReallocateSource(null)}
              disabled={reallocateMutation.isPending}
            >
              {t("actions.cancel")}
            </Button>
            <Button
              type='button'
              onClick={() => void handleReallocate()}
              disabled={reallocateMutation.isPending || !canReallocate}
            >
              {t("reallocate.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmActionDialog
        open={Boolean(pendingClose)}
        onOpenChange={(open) => !open && setPendingClose(null)}
        title={t("close.title")}
        description={t("close.description", { name: pendingClose ? pendingClose.name : "" })}
        confirmLabel={t("actions.close")}
        cancelLabel={t("actions.cancel")}
        confirmVariant='destructive'
        onConfirm={() => {
          if (!canEdit || !pendingClose) return
          void setFundStatusMutation
            .mutateAsync({ id: pendingClose.id, status: FinanceFundStatus.Closed })
            .then(() => {
              toast.success(t("toasts.closed"))
              setPendingClose(null)
            })
            .catch((error) => {
              toastError(error)
            })
        }}
      />
    </section>
  )
}
